import {useContext, useEffect, useCallback} from 'react'
import {UNSAFE_NavigationContext as NavigationContext} from 'react-router-dom'
import type {History, Blocker, Transition} from 'history'

/**
 * This is a modified version of the useBlocker hook that works with React Router v6.22.1
 * It adds a try/catch block to handle cases where navigator.block is not available
 * and provides a mock implementation for tests
 */
export const useBlocker = (blocker: Blocker, when = true) => {
  const {navigator} = useContext(NavigationContext)

  useEffect(() => {
    if (!when) return

    let unblock: (() => void) | undefined

    try {
      // Try to use the actual navigator.block method if available
      if (navigator && typeof (navigator as any).block === 'function') {
        unblock = (navigator as any).block((tx: Transition) => {
          const autoUnblockingTx = {
            ...tx,
            retry() {
              if (unblock) {
                unblock()
                tx.retry()
              }
            },
          }

          blocker(autoUnblockingTx)
        })
      } else {
        // Mock implementation for tests or when navigator.block is not available
        // This prevents the tests from failing with "navigator.block is not a function"
        console.warn(
          'Navigation blocking is not fully supported in this environment'
        )

        // Return a no-op function for tests
        unblock = () => {}
      }
    } catch (error) {
      console.error('Error setting up navigation blocker:', error)
      // Return a no-op function for tests
      unblock = () => {}
    }

    return unblock
  }, [navigator, blocker, when])
}
